import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faPercent } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { validateTaxForm, inputFilters } from '../../utils/validate';
import { useTranslation } from 'react-i18next';

/**
 * TaxForm
 *
 * Modal form for adding or editing tax rate groups. Supports gst/cgst/sgst/
 * igst fields with input filtering and validation via `validateTaxForm`.
 */
const TaxForm = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  tax = null, 
  loading = false
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    gst: '',
    cgst: '',
    sgst: '',
    igst: ''
  });
  const [errors, setErrors] = useState({});

  const isEditing = !!tax;
  const requiredIndicator = t('common.form.requiredIndicator');

  // Initialize form with tax data when editing
  // Populates the form when `tax` prop is provided, otherwise resets to defaults.
  useEffect(() => {
    if (isEditing && tax) {
      setFormData({
        gst: tax.gst?.toString() || '',
        cgst: tax.cgst?.toString() || '',
        sgst: tax.sgst?.toString() || '',
        igst: tax.igst?.toString() || ''
      });
    } else {
      setFormData({
        gst: '',
        cgst: '',
        sgst: '',
        igst: ''
      });
    }
    setErrors({});
  }, [tax, isEditing]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        gst: '',
        cgst: '',
        sgst: '',
        igst: ''
      });
      setErrors({});
    }
  }, [isOpen]);

  /**
   * handleInputChange(e)
   *
   * Filters input to numeric-only values and updates form state. Clears
   * the field error when the user starts typing.
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let filteredValue = value;
    
    // Apply input filters based on field type
    if (value) {
      filteredValue = inputFilters.numeric(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: filteredValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  /**
   * validateForm()
   *
   * Runs shared validation logic and sets `errors`. Returns true when the
   * form passes validation.
   */
  const validateForm = () => {
    const formErrors = validateTaxForm(formData);
    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  /**
   * handleSubmit(e)
   *
   * Validates and converts string inputs to floats before calling the
   * supplied `onSubmit` handler.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;               // guard double-submit (parent-driven loading)

    if (!validateForm()) {
      return;
    }
    
    const taxData = {
      gst: parseFloat(formData.gst),
      cgst: parseFloat(formData.cgst),
      sgst: parseFloat(formData.sgst),
      igst: parseFloat(formData.igst)
    };
    
    await onSubmit(taxData);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      {/* Hide scrollbars but keep scrolling functional */}
      <style>{`.tax-form-modal{ -ms-overflow-style: none; scrollbar-width: none; } .tax-form-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto tax-form-modal"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <h2 className="text-xl font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {isEditing ? t('taxes.forms.title.edit') : t('taxes.forms.title.add')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1)
            }}
            onMouseEnter={(e) => {
              if (!loading) e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2);
            }}
            onMouseLeave={(e) => {
              if (!loading) e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1);
            }}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* GST Rate */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                <FontAwesomeIcon icon={faPercent} className="mr-2" />
                {`${t('taxes.forms.fields.gst.label')} ${requiredIndicator}`}
              </label>
              <input
                type="text"
                name="gst"
                value={formData.gst}
                onChange={handleInputChange}
                placeholder={t('taxes.forms.fields.gst.placeholder')}
                disabled={loading}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.gst ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.gst && { backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) })
                }}
              />
              {errors.gst && <p className="text-sm font-medium mt-2" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.gst}</p>}
            </div>

            {/* CGST Rate */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                <FontAwesomeIcon icon={faPercent} className="mr-2" />
                {`${t('taxes.forms.fields.cgst.label')} ${requiredIndicator}`}
              </label>
              <input
                type="text"
                name="cgst"
                value={formData.cgst}
                onChange={handleInputChange}
                placeholder={t('taxes.forms.fields.cgst.placeholder')}
                disabled={loading}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.cgst ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.cgst && { backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) })
                }}
              />
              {errors.cgst && <p className="text-sm font-medium mt-2" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.cgst}</p>}
            </div>

            {/* SGST Rate */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                <FontAwesomeIcon icon={faPercent} className="mr-2" />
                {`${t('taxes.forms.fields.sgst.label')} ${requiredIndicator}`}
              </label>
              <input
                type="text"
                name="sgst"
                value={formData.sgst}
                onChange={handleInputChange}
                placeholder={t('taxes.forms.fields.sgst.placeholder')}
                disabled={loading}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.sgst ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.sgst && { backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) })
                }}
              />
              {errors.sgst && <p className="text-sm font-medium mt-2" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.sgst}</p>}
            </div>

            {/* IGST Rate */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                <FontAwesomeIcon icon={faPercent} className="mr-2" />
                {`${t('taxes.forms.fields.igst.label')} ${requiredIndicator}`}
              </label>
              <input
                type="text"
                name="igst"
                value={formData.igst}
                onChange={handleInputChange}
                placeholder={t('taxes.forms.fields.igst.placeholder')}
                disabled={loading}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.igst ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.igst && { backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) })
                }}
              />
              {errors.igst && <p className="text-sm font-medium mt-2" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.igst}</p>}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6" >
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:transform-none"
              style={{
                backgroundColor: 'transparent',
                color: UI_COLORS.TEXT.MUTED,
                border: `2px solid ${UI_COLORS.BORDERS.MEDIUM}`
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1);
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              {t('taxes.forms.buttons.cancel')}
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:transform-none"
              style={{
                backgroundColor: loading ? UI_COLORS.BUTTONS.PRIMARY.loading.background : UI_COLORS.BUTTONS.PRIMARY.background,
                color: loading ? UI_COLORS.BUTTONS.PRIMARY.loading.text : UI_COLORS.BUTTONS.PRIMARY.text
              }}
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{isEditing ? t('taxes.forms.buttons.submit.loading.edit') : t('taxes.forms.buttons.submit.loading.add')}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faSave} />
                  <span>{isEditing ? t('taxes.forms.buttons.submit.edit') : t('taxes.forms.buttons.submit.add')}</span>
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaxForm;